import * as t from "io-ts";
import { DateFromISOString } from "io-ts-types/lib/DateFromISOString";

import { OrderStatus, PaymentState } from "../constants";
import { Address } from "./address";
import { FinancingPlan } from "./financing";
import { DineroFromString } from "./utils";

const Order = t.readonly(
    t.type({
        token: t.string,
        number: t.string,
        date_placed: DateFromISOString,
        //
        basket_total_before_discounts_excl_tax: DineroFromString,
        basket_total_before_discounts_incl_tax: DineroFromString,
        //
        basket_total_excl_tax: DineroFromString,
        basket_total_incl_tax: DineroFromString,
        //
        shipping_excl_tax: DineroFromString,
        shipping_incl_tax: DineroFromString,
        //
        total_excl_tax: DineroFromString,
        total_incl_tax: DineroFromString,
    }),
);

export type IOrder = t.TypeOf<typeof Order>;

export const CompleteOrderPaymentInitData = t.readonly(
    t.type({
        basket: t.readonly(
            t.type({
                token: t.string,
            }),
        ),
        order: Order,
        financing_plans: t.readonlyArray(FinancingPlan),
        shipping_address: Address,
        billing_address: Address,
    }),
);

// Payment method types
type IPaymentMethod = Readonly<{
    enabled: boolean;
    pay_balance: boolean;
}>;

export type IPayment = Readonly<{
    "cash"?: IPaymentMethod;
    "pay-later"?: IPaymentMethod;
    "cybersource"?: IPaymentMethod;
    "financing"?: IPaymentMethod &
        Readonly<{
            account_number: string;
            financing_plan: number;
        }>;
}>;

export type IOrderResponse = Readonly<{
    number: string;
    status: string;
    [propName: string]: string;
}>;

export type IPaymentRejectMsgs = Readonly<{
    "cybersource": string;
    "financing": string;
    "cash": string;
    "pay-later": string;
    [key: string]: string;
}>;

// Cybersource types
export type ICybersourceTemplateContext = Readonly<{
    card_type: string;
    card_number: string;
    card_expiration: string;
    card_cvc: string;
    billing_first_name: string;
    billing_last_name: string;
    billing_phone_number: string;
    email: string;
    billing_line1: string;
    billing_line2: string;
    billing_line4: string;
    billing_postcode: string;
    billing_state: string;
    billing_country: string;
}>;

export type ICybersourceRequest = Readonly<{
    url: string;
    data: Readonly<{
        card_type?: string;
        card_number?: string;
        card_expiry_date?: string;
        card_cvn?: string;
        bill_to_forename?: string;
        bill_to_surname?: string;
        bill_to_phone?: string;
        bill_to_email?: string;
        bill_to_address_line1?: string;
        bill_to_address_line2?: string;
        bill_to_address_city?: string;
        bill_to_address_postal_code?: string;
        bill_to_address_state?: string;
        bill_to_address_country?: string;
    }>;
}>;

type ICybKeys = keyof ICybersourceRequest["data"];
type ICybDataKeys = keyof ICybersourceTemplateContext;
export type ICybersourceFieldMapping = {
    readonly [CybName in ICybKeys]: ICybDataKeys;
};

export type ICybersourceTemplate = Readonly<{
    url: string;
    fields: ReadonlyArray<
        Readonly<{
            key: ICybKeys;
            value: string;
            editable: boolean;
        }>
    >;
}>;

// Payment state types
export type IPaymentState = Readonly<{
    status: PaymentState;
    amount: string;
    required_action?: Readonly<{
        type: "form";
        name: "get-token" | "authorize";
        url: string;
        method: string;
        fields: ReadonlyArray<
            Readonly<{
                key: ICybKeys;
                value: string;
                editable: boolean;
            }>
        >;
    }>;
}>;

export type IPaymentStates = Readonly<{
    order_status: OrderStatus;
    payment_method_states: Readonly<{
        [code: string]: IPaymentState;
    }>;
}>;
